import React, {useCallback, useEffect, useRef, useState} from "react";
import socket, {emitNow, OFFLINE_TOAST} from "../utils/clientSocket";
import {useToasts} from "../components/ui/Toasts";
import Lobby from "../components/Lobby";
import Chat from "../components/Chat";
import CreateGameModal from "../components/CreateGameModal";
import ComputerSetupModal from "../components/ComputerSetupModal";
import PracticeBoard from "../components/PracticeBoard";
import RookChat from "../components/rook/RookChat";
import {RookFace, ROOK_IMAGE} from "../components/rook/RookAvatar";
import useLinePreview from "../components/rook/useLinePreview";
import {getSharedEngine} from "../utils/sharedEngine";
import Icon from "../components/ui/Icon";
import {useUser} from "../UserContext";
import {useOwnOffer} from "../utils/offerStore";
import {TimeVariants} from "../utils/gameEnums";
import useScenicBackground from "../utils/useScenicBackground";
import {useActiveGame} from "../utils/activeGameStore";
import {useNavigate} from "react-router-dom";

const QUICK_PLAY = [
    {minutes: 1, increment: 0, variant: TimeVariants.BULLET},
    {minutes: 3, increment: 2, variant: TimeVariants.BLITZ},
    {minutes: 5, increment: 0, variant: TimeVariants.BLITZ},
    {minutes: 10, increment: 0, variant: TimeVariants.RAPID},
    {minutes: 15, increment: 10, variant: TimeVariants.RAPID},
    {minutes: 30, increment: 0, variant: TimeVariants.CLASSIC}
];

const greeting = () => {
    const hour = new Date().getHours();
    if (hour < 5) return "Up late";
    if (hour < 11) return "Good morning";
    if (hour < 17) return "Good afternoon";
    return "Good evening";
};

const FrontPage = () => {
    useScenicBackground();
    const user = useUser();
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showComputerModal, setShowComputerModal] = useState(false);
    const [ownOffer, setOwnOffer] = useOwnOffer();
    const activeGame = useActiveGame();
    const navigate = useNavigate();

    const {push} = useToasts();

    // The chat card has two tabs: the public chat, and Rook, the coach who talks about the practice board
    // The chat card always opens on the public chat; the coach is one click away (and remembers your conversation)
    const [chatTab, setChatTab] = useState("everyone");
    const [practicePosition, setPracticePosition] = useState(null);
    const onPracticePosition = useCallback((position) => setPracticePosition(position), []);
    const preview = useLinePreview();
    // Clicking the robin in the heading opens his tab and brings the chat into view
    const chatCardRef = useRef(null);
    const openRook = () => {
        setChatTab("rook");
        chatCardRef.current?.scrollIntoView({behavior: "smooth", block: "nearest"});
    };
    const closePreview = preview.close;
    // Rook's lines belong to the Rook tab, so leaving it puts the board back
    useEffect(() => {
        if (chatTab !== "rook") closePreview();
    }, [chatTab, closePreview]);
    const quickPlay = (option) => {
        const isSame = ownOffer && Number(ownOffer.baseTime) === option.minutes
            && Number(ownOffer.increment) === option.increment && ownOffer.variant === "standard";
        if (isSame) {
            socket.emit("cancelOffer");
            setOwnOffer(null);
            return;
        }
        const sent = emitNow("createGameOffer", {
            baseTime: option.minutes,
            increment: option.increment,
            variant: "standard",
            selectedColor: "random",
            isCreatorsHandicap: false
        });
        if (!sent) push(OFFLINE_TOAST);
    };

    useEffect(() => {
        socket.emit("refreshLobby");
    }, [])

    // An open offer only makes sense while you are looking at the lobby. Cancel it when you leave the page
    // (or close the tab), so nobody joins a game you are not there for.
    const ownOfferRef = useRef(ownOffer);
    ownOfferRef.current = ownOffer;
    useEffect(() => {
        const cancelOwnOffer = () => {
            if (ownOfferRef.current) {
                socket.emit("cancelOffer");
            }
        };
        window.addEventListener("pagehide", cancelOwnOffer);
        return () => {
            window.removeEventListener("pagehide", cancelOwnOffer);
            if (ownOfferRef.current) {
                cancelOwnOffer();
                setOwnOffer(null);
            }
        };
    }, [setOwnOffer]);

    const isQuickSelected = (option) => ownOffer && ownOffer.variant === "standard"
        && Number(ownOffer.baseTime) === option.minutes && Number(ownOffer.increment) === option.increment;

    return (
        <div className="page">
            <div className="hero">
                <div className="hero__intro">
                    <button type="button" className="hero__rook" onClick={openRook} aria-label="Talk to Rook, your AI chess coach"
                            title="Talk to Rook">
                        <img src={ROOK_IMAGE} alt="" draggable="false"/>
                    </button>
                    <div>
                        <div className="eyebrow">{greeting()}{user?.username ? `, ${user.username}` : ""}</div>
                        <h1>Play chess with Rook by your side</h1>
                        <p>Meet Rook, your friendly AI chess coach. Or try Jumpstart, where games start mid-battle.</p>
                    </div>
                </div>
                <div className="hero__actions">
                    <button className="btn btn-lg" onClick={() => setShowComputerModal(true)}>
                        <Icon name="cpu"/> Play the computer
                    </button>
                    <button className="btn btn-primary btn-lg" onClick={() => setShowCreateModal(true)}>
                        <Icon name="plus"/> Create game
                    </button>
                </div>
            </div>

            {activeGame && (
                <div className="card active-game-banner">
                    <span className="pulse-dot"/>
                    <div style={{flex: 1, minWidth: 0}}>
                        <strong>You have a game in progress</strong>
                        <div className="small muted">against {activeGame.opponent?.username || "your opponent"}. Your clock is still running.</div>
                    </div>
                    <button className="btn btn-primary" onClick={() => navigate("/play", {state: activeGame})}>
                        Back to the game
                    </button>
                </div>
            )}

            <div className="play-layout">
                <section className="play-layout__lobby stack">
                    <div className="card">
                        <div className="card-header">
                            <div className="card-title"><Icon name="bolt"/> Quick play</div>
                            <button className="link-btn" onClick={() => setShowCreateModal(true)}>More options</button>
                        </div>
                        <div className="card-body">
                            <div className="quick-grid">
                                {QUICK_PLAY.map((option) => (
                                    <button key={`${option.minutes}+${option.increment}`} type="button"
                                            className={`quick-tile ${isQuickSelected(option) ? "selected" : ""}`}
                                            onClick={() => quickPlay(option)}
                                            aria-pressed={!!isQuickSelected(option)}>
                                        <span className="quick-tile__time">{option.minutes}+{option.increment}</span>
                                        <span className="quick-tile__label">
                                            <img src={option.variant.imageSrc} alt=""/>{option.variant.title}
                                        </span>
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>

                    <div className="card">
                        <div className="card-header">
                            <div className="card-title"><Icon name="users"/> Open games</div>
                            <button className="icon-btn icon-btn--sm" onClick={() => socket.emit("refreshLobby")}
                                    aria-label="Refresh lobby" title="Refresh">
                                <Icon name="refresh"/>
                            </button>
                        </div>
                        <div className="card-body" style={{paddingTop: "0.75rem"}}>
                            <Lobby ownOffer={ownOffer} setOwnOffer={setOwnOffer}/>
                        </div>
                    </div>
                </section>

                <section className="play-layout__board">
                    <PracticeBoard preview={preview} onPositionChange={onPracticePosition}/>
                </section>

                <section className="play-layout__chat">
                    <div className={`card chat-card ${chatTab === "rook" ? "chat-card--rook" : ""}`} ref={chatCardRef}>
                        <div className="card-header">
                            <div className="chat-tabs" role="tablist" aria-label="Chat">
                                <button type="button" role="tab" aria-selected={chatTab === "everyone"}
                                        className={chatTab === "everyone" ? "active" : ""} onClick={() => setChatTab("everyone")}>
                                    <Icon name="chat" size={16}/> Public chat
                                </button>
                                <button type="button" role="tab" aria-selected={chatTab === "rook"}
                                        className={chatTab === "rook" ? "active" : ""} onClick={() => setChatTab("rook")}>
                                    <RookFace size={20}/> AI coach Rook
                                </button>
                            </div>
                        </div>
                        <div style={{flex: 1, minHeight: 0}}>
                            {chatTab === "everyone" ? (
                                <Chat chatType={"global"} chatId={"global"} placeholder="Say hi, or ask @Rook…" withRook/>
                            ) : (
                                <RookChat position={practicePosition} mode="practice" getEngine={getSharedEngine}
                                          preview={preview} storeKey="practice"/>
                            )}
                        </div>
                    </div>
                </section>
            </div>

            <CreateGameModal
                showCreateModal={showCreateModal}
                closeModal={() => setShowCreateModal(false)}
            />
            <ComputerSetupModal open={showComputerModal} onClose={() => setShowComputerModal(false)}/>
        </div>
    );
}

export default FrontPage;
